import React, { useState, useCallback } from "react";
import { Box, Checkbox, Chip, Collapse, IconButton } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  Star as StarIcon,
  Close as CloseIcon,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ServiceBoardFilters as ServiceBoardFiltersType, ServiceBoardQuery, StarredItem } from "../models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import ServiceBoardMiniMap from "./ServiceBoardMiniMap";
import { dt } from "../../../shared/themes/redesignTokens";

// =====================================================================
//  DESIGN TOKENS (matching MapFilterPanel)
// =====================================================================

// Priority codes: 1=urgent, 2=high, 3=medium, 4=low
const priorityColors: Record<number, string> = {
  1: "#C0392B", // Urgent
  2: "#E67E22", // High
  3: dt.blueHover, // Medium
  4: "#27AE60", // Low
};

type DatePreset = "all" | "today" | "week" | "month";

const MAX_BRANDS_VISIBLE = 8;

// =====================================================================
//  HELPER COMPONENTS (same as MapFilterPanel)
// =====================================================================

interface CollapsibleSectionProps {
  title: string;
  sectionKey: string;
  expanded: boolean;
  activeCount: number;
  onToggle: (key: string) => void;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  sectionKey,
  expanded,
  activeCount,
  onToggle,
  children,
}) => (
  <Box>
    <Box
      onClick={() => onToggle(sectionKey)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        mb: expanded ? "8px" : "0px",
        py: "2px",
        borderRadius: "4px",
        transition: "all 0.15s ease",
        "&:hover": { backgroundColor: "#F8FAFC" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Box
          sx={{
            fontSize: "10px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: "#9CA3AF",
            fontFamily: dt.font,
            userSelect: "none",
          }}
        >
          {title}
        </Box>
        {/* Active filter badge (shown when collapsed with active filters) */}
        {!expanded && activeCount > 0 && (
          <Box
            sx={{
              fontSize: "9px",
              fontFamily: dt.fontMono,
              fontWeight: 700,
              color: "#FFFFFF",
              backgroundColor: dt.blue,
              borderRadius: "10px",
              minWidth: "16px",
              height: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 4px",
              lineHeight: 1,
            }}
          >
            {activeCount}
          </Box>
        )}
      </Box>
      {expanded ? (
        <ExpandLess sx={{ fontSize: "16px", color: "#9CA3AF", transition: "transform 0.2s" }} />
      ) : (
        <ExpandMore sx={{ fontSize: "16px", color: "#9CA3AF", transition: "transform 0.2s" }} />
      )}
    </Box>
    <Collapse in={expanded} timeout={200}>
      {children}
    </Collapse>
  </Box>
);

const Separator: React.FC = () => (
  <Box sx={{ borderTop: "1px dashed #E5E7EB", my: "10px" }} />
);

interface CheckboxRowProps {
  label: string;
  count?: number;
  checked: boolean;
  color: string;
  onChange: () => void;
}

const CheckboxRow: React.FC<CheckboxRowProps> = ({ label, count, checked, color, onChange }) => (
  <Box
    onClick={onChange}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "4px 4px 4px 0",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "background-color 0.15s",
      "&:hover": { backgroundColor: "#F8FAFC" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
      <Checkbox
        checked={checked}
        size="small"
        sx={{
          padding: "2px",
          color: "#D1D5DB",
          "&.Mui-checked": { color },
          "& .MuiSvgIcon-root": { fontSize: "16px" },
        }}
      />
      <Box
        sx={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
          opacity: checked ? 1 : 0.45,
          transition: "opacity 0.2s",
        }}
      />
      <Box
        sx={{
          fontSize: "12px",
          fontFamily: dt.font,
          fontWeight: checked ? 600 : 400,
          color: checked ? color : "#4B5563",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    </Box>
    {count !== undefined && (
      <Box
        sx={{
          fontSize: "10px",
          fontFamily: dt.fontMono,
          fontWeight: 600,
          color: "#9CA3AF",
          backgroundColor: "#F4F6F9",
          padding: "1px 6px",
          borderRadius: "8px",
          minWidth: "20px",
          textAlign: "center",
          lineHeight: "16px",
          flexShrink: 0,
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

// =====================================================================
//  DATE PICKER SLOT PROPS (matching MapFilterPanel)
// =====================================================================

const datePickerSlotProps = {
  textField: {
    size: "small" as const,
    fullWidth: true,
    sx: {
      "& .MuiOutlinedInput-root": {
        borderRadius: dt.radiusSm,
        fontFamily: dt.font,
        fontSize: "12px",
        backgroundColor: dt.inputBg,
        "& fieldset": { borderColor: "transparent" },
        "&:hover fieldset": { borderColor: dt.cardBorder },
        "&.Mui-focused fieldset": { borderColor: dt.blue, borderWidth: "1.5px" },
      },
      "& .MuiInputLabel-root": { fontFamily: dt.font, fontSize: "12px" },
    },
  },
};

// =====================================================================
//  MAIN COMPONENT
// =====================================================================

interface ServiceBoardFiltersProps {
  filters?: ServiceBoardFiltersType;
  query: ServiceBoardQuery;
  starredItems?: StarredItem[];
  onQueryChange: (query: ServiceBoardQuery) => void;
  onRemoveStarred?: (id: string) => void;
  onOpenMap?: () => void;
}

const ServiceBoardFiltersComponent = ({ filters, query, starredItems, onQueryChange, onRemoveStarred, onOpenMap }: ServiceBoardFiltersProps) => {
  const { translate } = useTranslation(["serviceBoard", "common"]);
  const navigate = useNavigate();
  const [showAllBrands, setShowAllBrands] = useState(false);

  // Collapsible section state (all expanded by default)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    miniMap: true,
    dateRange: true,
    quickFilters: true,
    priority: true,
    serviceType: true,
    machineBrand: true,
    country: true,
    starredItems: true,
  });

  // Date preset tracking (local state - the actual dateFrom/dateTo live in query)
  const [datePreset, setDatePreset] = useState<DatePreset>("all");

  // Toggle a collapsible section
  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // --- Date handlers ---
  const handleDateFromChange = (value: Dayjs | null) => {
    setDatePreset("all");
    onQueryChange({ ...query, dateFrom: value?.toISOString(), pageNo: 1 });
  };

  const handleDateToChange = (value: Dayjs | null) => {
    setDatePreset("all");
    onQueryChange({ ...query, dateTo: value?.toISOString(), pageNo: 1 });
  };

  const handleDatePreset = useCallback(
    (preset: DatePreset) => {
      setDatePreset(preset);
      let df: string | undefined;
      let dt2: string | undefined;
      if (preset === "today") {
        df = dayjs().startOf("day").toISOString();
        dt2 = dayjs().endOf("day").toISOString();
      } else if (preset === "week") {
        df = dayjs().startOf("week").add(1, "day").toISOString(); // Monday
        dt2 = dayjs().endOf("week").add(1, "day").toISOString(); // Sunday
      } else if (preset === "month") {
        df = dayjs().startOf("month").toISOString();
        dt2 = dayjs().endOf("month").toISOString();
      }
      onQueryChange({ ...query, dateFrom: df, dateTo: dt2, pageNo: 1 });
    },
    [query, onQueryChange]
  );

  // --- Quick filter handlers ---
  const handleUrgentToggle = () => {
    onQueryChange({ ...query, urgentOnly: !query.urgentOnly, pageNo: 1 });
  };

  const handleStarredToggle = () => {
    onQueryChange({ ...query, starredOnly: !query.starredOnly, pageNo: 1 });
  };

  // --- Priority handler ---
  const handlePriorityToggle = (code: number) => {
    const current = query.priorityTypeCodes || [];
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    onQueryChange({ ...query, priorityTypeCodes: next.length > 0 ? next : undefined, pageNo: 1 });
  };

  // --- Service type handler ---
  const handleServiceTypeToggle = (code: number) => {
    const current = query.serviceTypeCodes || [];
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    onQueryChange({ ...query, serviceTypeCodes: next.length > 0 ? next : undefined, pageNo: 1 });
  };

  // --- Machine brand handler ---
  const handleBrandToggle = (brandId: string) => {
    const current = query.machineBrandIds || [];
    const next = current.includes(brandId)
      ? current.filter((id) => id !== brandId)
      : [...current, brandId];
    onQueryChange({ ...query, machineBrandIds: next.length > 0 ? next : undefined, pageNo: 1 });
  };

  // --- Country handler ---
  const handleCountryToggle = (countryId: number) => {
    const current = query.countryIds || [];
    const next = current.includes(countryId)
      ? current.filter((id) => id !== countryId)
      : [...current, countryId];
    onQueryChange({ ...query, countryIds: next.length > 0 ? next : undefined, pageNo: 1 });
  };

  // --- Clear all filters ---
  const handleClearFilters = () => {
    setDatePreset("all");
    onQueryChange({
      ...query,
      urgentOnly: false,
      starredOnly: false,
      dateFrom: undefined,
      dateTo: undefined,
      machineBrandIds: undefined,
      priorityTypeCodes: undefined,
      serviceTypeCodes: undefined,
      countryIds: undefined,
      pageNo: 1,
    });
  };

  // Computed values
  const hasActiveFilters =
    query.urgentOnly ||
    query.starredOnly ||
    query.dateFrom ||
    query.dateTo ||
    (query.machineBrandIds && query.machineBrandIds.length > 0) ||
    (query.priorityTypeCodes && query.priorityTypeCodes.length > 0) ||
    (query.serviceTypeCodes && query.serviceTypeCodes.length > 0) ||
    (query.countryIds && query.countryIds.length > 0);

  const machineBrands = filters?.machineBrands ?? [];
  const visibleBrands = showAllBrands ? machineBrands : machineBrands.slice(0, MAX_BRANDS_VISIBLE);
  const countries = filters?.countries ?? [];

  // Active filter counts per section (for badges on collapsed headers)
  const sectionActiveCount: Record<string, number> = {
    miniMap: 0,
    dateRange: (query.dateFrom ? 1 : 0) + (query.dateTo ? 1 : 0),
    quickFilters: (query.urgentOnly ? 1 : 0) + (query.starredOnly ? 1 : 0),
    priority: query.priorityTypeCodes?.length ?? 0,
    serviceType: query.serviceTypeCodes?.length ?? 0,
    machineBrand: query.machineBrandIds?.length ?? 0,
    country: query.countryIds?.length ?? 0,
    starredItems: 0, // bookmarks section doesn't count as a "filter"
  };

  const datePresets: { key: DatePreset; labelKey: string }[] = [
    { key: "all", labelKey: "mapFilterAllTime" },
    { key: "today", labelKey: "mapFilterToday" },
    { key: "week", labelKey: "mapFilterThisWeek" },
    { key: "month", labelKey: "mapFilterThisMonth" },
  ];

  // Priority codes: 1=urgent, 2=high, 4=low
  const priorityItems: { code: number; labelKey: string }[] = [
    { code: 1, labelKey: "urgent" },
    { code: 2, labelKey: "priorityHigh" },
    { code: 4, labelKey: "priorityLow" },
  ];

  const serviceTypeItems: { code: number; labelKey: string }[] = [
    { code: 1, labelKey: "mapFilterRepair" },
    { code: 2, labelKey: "mapFilterParts" },
    { code: 3, labelKey: "mapFilterMaintenance" },
  ];

  // =====================================================================
  //  RENDER
  // =====================================================================

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {/* MINI MAP (click opens full map view) */}
      {onOpenMap && (
        <>
          <CollapsibleSection
            title={translate("viewMap")}
            sectionKey="miniMap"
            expanded={expandedSections.miniMap}
            activeCount={sectionActiveCount.miniMap}
            onToggle={toggleSection}
          >
            {expandedSections.miniMap && (
              <ServiceBoardMiniMap
                onOpenMap={onOpenMap}
              />
            )}
          </CollapsibleSection>

          <Separator />
        </>
      )}

      {/* DATE RANGE */}
      <CollapsibleSection
        title={translate("dateRange")}
        sectionKey="dateRange"
        expanded={expandedSections.dateRange}
        activeCount={sectionActiveCount.dateRange}
        onToggle={toggleSection}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: "8px" }}>
            <DatePicker
              label={translate("from")}
              value={query.dateFrom ? dayjs(query.dateFrom) : null}
              onChange={handleDateFromChange}
              slotProps={datePickerSlotProps}
            />
            <DatePicker
              label={translate("to")}
              value={query.dateTo ? dayjs(query.dateTo) : null}
              onChange={handleDateToChange}
              slotProps={datePickerSlotProps}
            />
          </Box>
        </LocalizationProvider>

        {/* DATE PRESETS */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "4px" }}>
          {datePresets.map((p) => (
            <Chip
              key={p.key}
              label={translate(p.labelKey)}
              size="small"
              onClick={() => handleDatePreset(p.key)}
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 500,
                height: "26px",
                borderRadius: dt.radiusPill,
                backgroundColor: datePreset === p.key ? dt.blue : "transparent",
                color: datePreset === p.key ? "#FFFFFF" : dt.textSecondary,
                border: `1px solid ${datePreset === p.key ? dt.blue : dt.cardBorder}`,
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: datePreset === p.key ? dt.blueHover : dt.blueSoft,
                },
              }}
            />
          ))}
        </Box>
      </CollapsibleSection>

      <Separator />

      {/* QUICK FILTERS (Urgent Only + Starred) */}
      <CollapsibleSection
        title={translate("showOnly")}
        sectionKey="quickFilters"
        expanded={expandedSections.quickFilters}
        activeCount={sectionActiveCount.quickFilters}
        onToggle={toggleSection}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
          <CheckboxRow
            label={translate("urgentOnly")}
            checked={query.urgentOnly ?? false}
            color="#C0392B"
            onChange={handleUrgentToggle}
          />
          <CheckboxRow
            label={translate("starred")}
            count={starredItems?.length ?? 0}
            checked={query.starredOnly ?? false}
            color="#F5A623"
            onChange={starredItems?.length ? handleStarredToggle : () => {}}
          />
        </Box>
      </CollapsibleSection>

      <Separator />

      {/* PRIORITY */}
      <CollapsibleSection
        title={translate("mapFilterPriority")}
        sectionKey="priority"
        expanded={expandedSections.priority}
        activeCount={sectionActiveCount.priority}
        onToggle={toggleSection}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
          {priorityItems.map((item) => (
            <CheckboxRow
              key={item.code}
              label={translate(item.labelKey)}
              checked={(query.priorityTypeCodes || []).includes(item.code)}
              color={priorityColors[item.code]}
              onChange={() => handlePriorityToggle(item.code)}
            />
          ))}
        </Box>
      </CollapsibleSection>

      <Separator />

      {/* SERVICE TYPE */}
      <CollapsibleSection
        title={translate("mapFilterServiceType")}
        sectionKey="serviceType"
        expanded={expandedSections.serviceType}
        activeCount={sectionActiveCount.serviceType}
        onToggle={toggleSection}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
          {serviceTypeItems.map((item) => (
            <CheckboxRow
              key={item.code}
              label={translate(item.labelKey)}
              checked={(query.serviceTypeCodes || []).includes(item.code)}
              color={dt.blue}
              onChange={() => handleServiceTypeToggle(item.code)}
            />
          ))}
        </Box>
      </CollapsibleSection>

      {/* MACHINE BRAND -- only show if there are brands */}
      {machineBrands.length > 0 && (
        <>
          <Separator />
          <CollapsibleSection
            title={translate("machineBrand")}
            sectionKey="machineBrand"
            expanded={expandedSections.machineBrand}
            activeCount={sectionActiveCount.machineBrand}
            onToggle={toggleSection}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
              {visibleBrands.map((brand) => (
                <CheckboxRow
                  key={brand.id}
                  label={brand.name}
                  count={brand.count}
                  checked={(query.machineBrandIds || []).includes(brand.id)}
                  color={dt.blue}
                  onChange={() => handleBrandToggle(brand.id)}
                />
              ))}
            </Box>
            {machineBrands.length > MAX_BRANDS_VISIBLE && (
              <Box
                onClick={() => setShowAllBrands(!showAllBrands)}
                sx={{
                  fontSize: "12px",
                  color: dt.blue,
                  cursor: "pointer",
                  fontWeight: 500,
                  fontFamily: dt.font,
                  mt: "4px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {showAllBrands
                  ? translate("common:showLess")
                  : `${translate("common:showMore")} (${machineBrands.length - MAX_BRANDS_VISIBLE})`}
              </Box>
            )}
          </CollapsibleSection>
        </>
      )}

      {/* COUNTRY -- only show if there are countries */}
      {countries.length > 0 && (
        <>
          <Separator />
          <CollapsibleSection
            title={translate("mapFilterCountry")}
            sectionKey="country"
            expanded={expandedSections.country}
            activeCount={sectionActiveCount.country}
            onToggle={toggleSection}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
              {countries.map((c) => (
                <CheckboxRow
                  key={c.id}
                  label={c.name}
                  count={c.count}
                  checked={(query.countryIds || []).includes(c.id)}
                  color={dt.blue}
                  onChange={() => handleCountryToggle(c.id)}
                />
              ))}
            </Box>
          </CollapsibleSection>
        </>
      )}


      {/* STARRED ITEMS (bookmarked requests) */}
      {starredItems && starredItems.length > 0 && (
        <>
          <Separator />
          <CollapsibleSection
            title={translate("starred")}
            sectionKey="starredItems"
            expanded={expandedSections.starredItems}
            activeCount={sectionActiveCount.starredItems}
            onToggle={toggleSection}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
              {starredItems.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "4px 4px 4px 0",
                    borderRadius: "6px",
                    transition: "background-color 0.15s",
                    "&:hover": { backgroundColor: "#FFF8E1" },
                  }}
                >
                  <Box
                    onClick={() => navigate(`/service-board/${item.id}`)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <StarIcon sx={{ fontSize: "14px", color: "#F5A623", flexShrink: 0 }} />
                    <Box
                      sx={{
                        fontSize: "12px",
                        fontFamily: dt.fontMono,
                        fontWeight: 500,
                        color: dt.blue,
                        letterSpacing: "0.3px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {item.internalNumber}
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onRemoveStarred?.(item.id); }}
                    sx={{
                      padding: "2px",
                      color: dt.textMuted,
                      "&:hover": { color: "#C0392B", backgroundColor: "rgba(192,57,43,0.08)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </CollapsibleSection>
        </>
      )}

      {/* CLEAR FILTERS (at bottom, same as map's Reset All) */}
      {hasActiveFilters && (
        <Box sx={{ mt: "8px", mb: "8px" }}>
          <Box
            onClick={handleClearFilters}
            sx={{
              fontSize: "12px",
              color: dt.blue,
              cursor: "pointer",
              fontWeight: 500,
              fontFamily: dt.font,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {translate("clearFilters")}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ServiceBoardFiltersComponent;
